import { Box, Button, Chip, LinearProgress, Link, Stack, Typography } from '@mui/material';
import {
  CelebrationRounded,
  EventAvailableRounded,
  FlagRounded,
  HistoryEduRounded,
  MenuBookRounded,
  PictureAsPdfOutlined,
  PlaceRounded,
} from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import type { StudentStatus } from '@dosfilos/domain';
import { credentialProgressApi } from '../../../lib/api/credential-progress.api';
import type { CredentialProgress } from '../../../lib/api/credential-progress.api';
import { db } from '../../../lib/firebase';
import { formatDate } from '../../../lib/i18n-format';
import { useTenantConfig } from '../../../hooks/use-tenant-config';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import { todayInZoneWeb } from '@dosfilos/shared';
import { useTenantStore, useTenantTimeZone } from '../../../stores/tenant.store';
import { usePortalCacheScope, usePortalViewing } from '../../../stores/portal-view.store';
import { formatGpa, progressFor } from '../../sis/components/academic-record/academic-record.model';
import { useRecordPdf } from '../../sis/components/academic-record/use-record-pdf';
import { useEnrolledSections, useStudentProgramEnrollments } from '../hooks/use-portal-data';
import {
  currentTermIdOf,
  greetingName,
  homeLayout,
  nextSteps,
  programNamesOf,
  recordSummary,
  type HomeBlock,
  type NextStep,
  type PortalContact,
  type ProgramEnrollmentLite,
} from './portal-home.model';

// ─── El inicio del portal: bienvenida, expediente, qué sigue y contacto ─────
// Nunca una pantalla vacía. Con `graduation_audit`, el resumen del expediente
// (el mismo cálculo que la pestaña de la credencial, con audiencia «alumno»)
// va arriba de los cursos; sin cursos, ocupa su lugar. Sin el flag, el inicio
// es el de siempre con la bienvenida y el contacto.
//
// En «Ver portal» el alumno se nombra por su código, y el enlace para
// inscribirse se ve pero no hace nada: la vista es de solo lectura.

const SURFACE = {
  bgcolor: 'rgba(255,255,255,0.02)',
  border: '1px solid #262c36',
  borderRadius: 3,
  p: { xs: 2.5, sm: 3 },
} as const;

const STATUS_CHIP: Record<StudentStatus, { bg: string; fg: string }> = {
  graduated: { bg: 'rgba(96,165,250,0.16)', fg: '#93c5fd' },
  active: { bg: 'rgba(16,185,129,0.16)', fg: '#6ee7b7' },
  inactive: { bg: 'rgba(139,148,158,0.16)', fg: '#c9d1d9' },
};

/** Todo lo que el inicio necesita, de lecturas que el portal ya hace. */
export function usePortalHome(courses: { any: boolean; visible: boolean }) {
  const config = useTenantConfig();
  const { currentTenantId } = useTenantStore();
  const scope = usePortalCacheScope();
  const tenantTz = useTenantTimeZone();
  // M4·D1: el expediente por credenciales (configuración, con la bandera de respaldo).
  const flag = config.academicRecord.mode === 'credentials';
  const recordQ = useQuery({
    // La MISMA clave que la página del expediente: una sola lectura para las dos.
    queryKey: ['portal-record', currentTenantId, scope],
    enabled: !!currentTenantId && config.settled && flag,
    queryFn: () => credentialProgressApi.getOwn({ tenantId: currentTenantId! }),
    staleTime: 60_000,
    retry: false,
  });
  const sectionsQ = useEnrolledSections();
  const programsQ = useStudentProgramEnrollments();
  const programEnrollments = programsQ.data?.items ?? [];
  const progress = recordQ.data ? progressFor(recordQ.data, 'student') : null;
  const steps = progress
    ? nextSteps({ progress, enrollment: sectionsQ.data, programEnrollments })
    : [];
  const blocks = homeLayout({
    // Sin expediente legible (una cuenta sin persona vinculada), el inicio de siempre.
    credentialRecord: flag && !!progress,
    hasCourses: courses.any,
    hasVisibleCourses: courses.visible,
    hasNextStep: steps.length > 0,
    hasContact: !!config.contact,
  });
  return {
    ready: config.settled && !(flag && (recordQ.isLoading || programsQ.isLoading)),
    blocks,
    progress,
    programEnrollments,
    currentTermId: currentTermIdOf(sectionsQ.data, todayInZoneWeb(tenantTz)),
    steps,
    contact: config.contact,
  };
}

/** Los bloques en su orden. `courses` y `empty` son lo que el inicio mostraba hasta hoy. */
export function PortalHomeFrame({
  home,
  courses,
  empty,
}: {
  home: ReturnType<typeof usePortalHome>;
  courses: React.ReactNode;
  empty: React.ReactNode;
}): JSX.Element {
  const render = (b: HomeBlock): React.ReactNode => {
    switch (b) {
      case 'welcome':
        return <WelcomeCard key={b} status={home.progress?.studentStatus ?? null} />;
      case 'summary':
        return home.progress ? (
          <RecordSummaryCard
            key={b}
            progress={home.progress}
            programEnrollments={home.programEnrollments}
            currentTermId={home.currentTermId}
          />
        ) : null;
      case 'nextStep':
        return <NextStepCard key={b} steps={home.steps} />;
      case 'courses':
        return <Box key={b}>{courses}</Box>;
      case 'empty':
        return <Box key={b}>{empty}</Box>;
      case 'contact':
        return home.contact ? <ContactCard key={b} contact={home.contact} /> : null;
    }
  };
  return <Stack spacing={3}>{home.blocks.map(render)}</Stack>;
}

function WelcomeCard({ status }: { status: StudentStatus | null }): JSX.Element {
  const { t } = useTranslation('sis');
  const viewing = usePortalViewing();
  const identity = useStudentIdentity();
  const { currentTenantId } = useTenantStore();
  const programsQ = useStudentProgramEnrollments();
  const namesQ = useQuery({
    queryKey: ['portal-program-names', currentTenantId],
    enabled: !!currentTenantId,
    staleTime: 5 * 60_000,
    queryFn: async () => {
      const snap = await getDocs(collection(db, `tenants/${currentTenantId}/programs`));
      return new Map(snap.docs.map((d) => [d.id, (d.data()['name'] as string) ?? '']));
    },
  });
  const name = greetingName({
    viewing,
    viewingCode: identity.studentCode,
    firstName: identity.firstName,
  });
  const programs = programNamesOf(programsQ.data?.items ?? [], namesQ.data ?? new Map());
  // En la vista el código ya está en el saludo.
  const code = viewing ? null : identity.studentCode;

  return (
    <Box
      sx={{
        ...SURFACE,
        background: 'linear-gradient(135deg, #1a2232 0%, #141a24 60%, #12161d 100%)',
        border: '1px solid #21262d',
      }}
    >
      <Typography variant="h4" fontWeight={800} color="#e6edf3">
        {name ? t('portal.home.hello', { name }) : t('portal.home.helloNoName')}
      </Typography>
      <Typography variant="body1" color="#8b949e" sx={{ mt: 0.5 }}>
        {t('portal.home.welcome')}
      </Typography>
      {(code || programs.length > 0 || status) && (
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
          sx={{ mt: 2 }}
        >
          {(code || programs.length > 0) && (
            <Typography variant="body2" color="#c9d1d9" sx={{ fontVariantNumeric: 'tabular-nums' }}>
              {[code ? t('portal.home.code', { code }) : null, ...programs]
                .filter(Boolean)
                .join(' · ')}
            </Typography>
          )}
          {status && (
            <Chip
              size="small"
              label={t(`portal.home.status.${status}`)}
              sx={{
                height: 22,
                fontWeight: 700,
                bgcolor: STATUS_CHIP[status].bg,
                color: STATUS_CHIP[status].fg,
              }}
            />
          )}
        </Stack>
      )}
    </Box>
  );
}

function RecordSummaryCard({
  progress,
  programEnrollments,
  currentTermId,
}: {
  progress: CredentialProgress;
  programEnrollments: readonly ProgramEnrollmentLite[];
  currentTermId: string | null;
}): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const pdf = useRecordPdf(progress, 'student');
  const s = recordSummary(progress, programEnrollments, currentTermId);
  const tiles: Array<{ label: string; value: string }> = [
    {
      label: t('portal.home.summary.credits'),
      value:
        s.unitsRequired === null
          ? String(s.unitsEarned)
          : t('portal.home.summary.creditsOf', {
              earned: s.unitsEarned,
              required: s.unitsRequired,
            }),
    },
    { label: t('portal.home.summary.coursesPassed'), value: String(s.coursesPassed) },
    // Con un promedio que mostrar, en cualquier escala (antes: solo con letras).
    ...(s.gpa !== null ? [{ label: t('portal.home.summary.gpa'), value: formatGpa(s.gpa) }] : []),
    {
      label: s.lastTermIsCurrent
        ? t('portal.home.summary.currentTerm')
        : t('portal.home.summary.lastTerm'),
      value: s.lastTermName ?? '—',
    },
  ];

  return (
    <Box sx={SURFACE}>
      <Typography variant="h6" fontWeight={700} color="#e6edf3">
        {t('portal.home.summary.title')}
      </Typography>
      <Box
        sx={{
          mt: 2,
          display: 'grid',
          gap: 2,
          gridTemplateColumns: {
            xs: 'repeat(2, minmax(0, 1fr))',
            md: `repeat(${tiles.length}, minmax(0, 1fr))`,
          },
        }}
      >
        {tiles.map((tile) => (
          <Box key={tile.label} sx={{ minWidth: 0 }}>
            <Typography variant="caption" color="#8b949e">
              {tile.label}
            </Typography>
            <Typography
              variant="h6"
              fontWeight={700}
              color="#e6edf3"
              sx={{ fontVariantNumeric: 'tabular-nums', overflowWrap: 'anywhere' }}
            >
              {tile.value}
            </Typography>
          </Box>
        ))}
      </Box>

      {s.credential && (
        <Box sx={{ mt: 2.5 }}>
          <Stack direction="row" justifyContent="space-between" spacing={2}>
            <Typography variant="body2" color="#c9d1d9">
              {t('portal.home.summary.toward', { credential: s.credential.name })}
            </Typography>
            <Typography
              variant="body2"
              fontWeight={700}
              color="#e6edf3"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {`${s.credential.percent}%`}
            </Typography>
          </Stack>
          <LinearProgress
            variant="determinate"
            value={s.credential.percent}
            aria-label={t('portal.home.summary.toward', { credential: s.credential.name })}
            sx={{ mt: 1, height: 8, borderRadius: 4, bgcolor: '#21262d' }}
          />
        </Box>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ mt: 2.5 }}>
        <Button
          variant="contained"
          startIcon={<HistoryEduRounded />}
          onClick={() => navigate('/portal-v2/historial')}
        >
          {t('portal.home.openRecord')}
        </Button>
        <Button variant="outlined" startIcon={<PictureAsPdfOutlined />} onClick={pdf.download}>
          {t('portal.home.downloadTranscript')}
        </Button>
      </Stack>
      {pdf.error && (
        <Typography variant="body2" color="#f87171" sx={{ mt: 1 }}>
          {pdf.error}
        </Typography>
      )}
    </Box>
  );
}

function NextStepCard({ steps }: { steps: NextStep[] }): JSX.Element {
  const { t } = useTranslation('sis');
  const navigate = useNavigate();
  const viewing = usePortalViewing();
  return (
    <Box sx={SURFACE}>
      <Typography variant="h6" fontWeight={700} color="#e6edf3" sx={{ mb: 1.5 }}>
        {t('portal.home.next.title')}
      </Typography>
      <Stack spacing={1.5}>
        {steps.map((step) => (
          <Stack key={step.kind} direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{ color: '#8b949e', pt: 0.25 }}>
              {step.kind === 'graduated' ? (
                <CelebrationRounded fontSize="small" />
              ) : step.kind === 'missing' ? (
                <FlagRounded fontSize="small" />
              ) : step.kind === 'inProgress' ? (
                <MenuBookRounded fontSize="small" />
              ) : (
                <EventAvailableRounded fontSize="small" />
              )}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="body1" color="#e6edf3">
                {step.kind === 'graduated'
                  ? step.on
                    ? t('portal.home.next.graduatedOn', {
                        date: formatDate(`${step.on}T12:00:00`, { dateStyle: 'long' }),
                      })
                    : t('portal.home.next.graduated')
                  : step.kind === 'missing'
                    ? t('portal.home.next.missing', {
                        count: step.courses,
                        credential: step.credential,
                      })
                    : step.kind === 'inProgress'
                      ? t('portal.home.next.inProgress', { count: step.courses })
                      : t('portal.home.next.enrollOpen', { term: step.termName })}
              </Typography>
              {step.kind === 'missing' && step.inProgress > 0 && (
                <Typography variant="body2" color="#8b949e">
                  {t('portal.home.next.inProgressOfThem', { count: step.inProgress })}
                </Typography>
              )}
              {step.kind === 'enroll' && (
                <Button
                  size="small"
                  variant="outlined"
                  sx={{ mt: 1 }}
                  // En la vista el aviso se ve, pero inscribirse no es de solo lectura.
                  disabled={viewing}
                  onClick={() => navigate('/portal-v2/enroll')}
                >
                  {t('portal.home.next.enrollAction')}
                </Button>
              )}
            </Box>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
}

function ContactCard({ contact }: { contact: PortalContact }): JSX.Element {
  const { t } = useTranslation('sis');
  return (
    <Box sx={SURFACE}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box sx={{ color: '#8b949e', pt: 0.25 }}>
          <PlaceRounded fontSize="small" />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} color="#e6edf3">
            {t('portal.home.contact.title')}
          </Typography>
          {contact.address && (
            <Typography variant="body2" color="#c9d1d9">
              {contact.address}
            </Typography>
          )}
          {contact.phone && (
            <Typography variant="body2" color="#c9d1d9">
              {t('portal.home.contact.phone', { phone: contact.phone })}
            </Typography>
          )}
          {contact.mapsUrl && (
            <Link
              href={contact.mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="body2"
              sx={{ color: '#58a6ff' }}
            >
              {t('portal.home.contact.map')}
            </Link>
          )}
        </Box>
      </Stack>
    </Box>
  );
}
